import {
  BoxGeometry,
  BufferGeometry,
  Color,
  CylinderGeometry,
  Float32BufferAttribute,
  Group,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PointLight,
  Quaternion,
  SphereGeometry,
  SpotLight,
  Vector3,
} from "three";

import {
  MAPPED_ROAD_VEHICLE_FAMILY_PROFILES,
  MAPPED_ROAD_VEHICLE_TIRE_VISUAL_SINK_M,
  ROAD_VEHICLE_BODY_COLORS_BY_ID,
  ROAD_VEHICLE_VISUAL_TUNING,
  ROAD_VEHICLE_WHEEL_HALF_TRACK_BODY_SCALE,
  VILNIUS_ROAD_VEHICLES,
  type RoadVehicleSpec,
} from "../../config/vehicles.ts";
import type { RoadVehiclePresentation } from "../../sim/vehicles/index.ts";

interface WheelPivot {
  readonly steering: Group;
  readonly rolling: Group;
  readonly baseY: number;
  readonly front: boolean;
}

interface AnimatedNode {
  readonly object: Object3D;
  readonly baseQuaternion: Quaternion;
}

interface LampMaterial {
  readonly material: MeshStandardMaterial;
  readonly baseEmissive: Color;
  readonly baseIntensity: number;
  readonly side: -1 | 1;
}

interface DoorVisual {
  readonly pivot: Group;
  readonly side: "left" | "right";
}

interface VehicleVisual {
  readonly root: Object3D;
  readonly wheels: readonly WheelPivot[];
  readonly steeringWheels: readonly AnimatedNode[];
  readonly headlamps: readonly LampMaterial[];
  readonly tailLamps: readonly LampMaterial[];
  readonly beaconLamps: readonly LampMaterial[];
  readonly reverseLamps: readonly Mesh[];
  readonly leftIndicators: readonly Mesh[];
  readonly rightIndicators: readonly Mesh[];
  readonly doors: readonly DoorVisual[];
  readonly headlights: readonly SpotLight[];
  readonly beaconLight: PointLight;
  readonly damagePatches: readonly Mesh[];
  readonly impactParticles: readonly Mesh[];
}

const specsById = new Map(VILNIUS_ROAD_VEHICLES.map((spec) => [spec.id, spec]));
const rollAxis = new Vector3(0, 0, 1);
const scratchQuaternion = new Quaternion();

export class RoadVehicleViewController {
  readonly object = new Group();
  readonly #visuals = new Map<string, VehicleVisual>();
  readonly #proceduralRoots = new Map<string, Object3D>();

  constructor() {
    this.object.name = "procedural-road-vehicles";
  }

  setPresentation(
    presentation: Readonly<RoadVehiclePresentation>,
    root: Object3D | null,
    suppliedSpec?: Readonly<RoadVehicleSpec>,
  ): void {
    const spec = suppliedSpec ?? specsById.get(presentation.id);
    if (spec === undefined) return;
    if (root === null && spec.proceduralBodyColorHex !== undefined) {
      root = this.#proceduralRoots.get(presentation.id) ??
        createProceduralRoadVehicleRoot(spec);
      if (!this.#proceduralRoots.has(presentation.id)) {
        this.#proceduralRoots.set(presentation.id, root);
        this.object.add(root);
      }
    }
    if (root === null) return;
    let visual = this.#visuals.get(presentation.id);
    if (visual === undefined || visual.root !== root) {
      visual = createVehicleVisual(root, spec);
      this.#visuals.set(presentation.id, visual);
    }
    updateVehicleVisual(visual, presentation);
  }

  retainPresentations(vehicleIds: ReadonlySet<string>): void {
    for (const [vehicleId, root] of this.#proceduralRoots) {
      if (vehicleIds.has(vehicleId)) continue;
      this.object.remove(root);
      disposeProceduralRoadVehicleRoot(root);
      this.#proceduralRoots.delete(vehicleId);
      this.#visuals.delete(vehicleId);
    }
  }

  clear(): void {
    for (const root of this.#proceduralRoots.values()) {
      disposeProceduralRoadVehicleRoot(root);
    }
    this.object.clear();
    this.#proceduralRoots.clear();
    this.#visuals.clear();
  }
}

function createProceduralRoadVehicleRoot(
  spec: Readonly<RoadVehicleSpec>,
): Group {
  const root = new Group();
  root.name = `procedural-road-vehicle:${spec.id}`;
  root.userData = { proceduralRoadVehicle: true, vehicleId: spec.id };
  const bodyColor = spec.proceduralBodyColorHex ?? 0xc8cbc5;
  const familyProfile = spec.proceduralFamily === undefined
    ? null
    : MAPPED_ROAD_VEHICLE_FAMILY_PROFILES[spec.proceduralFamily];
  const bodyMaterial = new MeshStandardMaterial({
    color: bodyColor,
    roughness: 0.52,
    metalness: 0.16,
  });
  const body = new Mesh(
    new BoxGeometry(spec.bodySizeM.x, 0.62, spec.bodySizeM.z),
    bodyMaterial,
  );
  body.name = "procedural-body";
  body.position.y = 0.47;
  body.castShadow = true;
  body.receiveShadow = true;
  root.add(body);

  const glassMaterial = new MeshStandardMaterial({
    color: 0x40545b,
    roughness: 0.5,
    metalness: 0.02,
  });
  const cabinHeightM = familyProfile?.cabinHeightM ?? 0.5;
  const cabinLengthM = spec.bodySizeM.z *
    (familyProfile?.cabinLengthScale ?? 0.49);
  const roofLengthM = spec.bodySizeM.z *
    (familyProfile?.roofLengthScale ?? 0.42);
  const cabinBottomWidthM = spec.bodySizeM.x * 0.84;
  const cabinTopWidthM = spec.bodySizeM.x *
    (spec.proceduralFamily === "compact-van" ? 0.77 :
      spec.proceduralFamily === "older-boxy-car" ? 0.74 : 0.68);
  const cabin = new Mesh(
    createTrapezoidCabinGeometry(
      cabinBottomWidthM,
      cabinTopWidthM,
      cabinHeightM,
      cabinLengthM,
      roofLengthM,
    ),
    glassMaterial,
  );
  cabin.name = "procedural-cabin-glass";
  cabin.position.set(
    0,
    0.91 + cabinHeightM * 0.5,
    familyProfile?.cabinOffsetZ ?? -0.05,
  );
  cabin.castShadow = true;
  root.add(cabin);

  const roof = new Mesh(
    new BoxGeometry(
      cabinTopWidthM * 1.035,
      0.1,
      roofLengthM + 0.06,
    ),
    bodyMaterial.clone(),
  );
  roof.name = "procedural-roof";
  roof.position.set(
    0,
    0.94 + cabinHeightM,
    familyProfile?.cabinOffsetZ ?? -0.05,
  );
  roof.castShadow = true;
  root.add(roof);

  const wheelMaterial = new MeshStandardMaterial({
    color: 0x202322,
    roughness: 0.9,
    metalness: 0.02,
  });
  for (const [axle, z] of [["front", spec.wheelbaseM / 2], ["rear", -spec.wheelbaseM / 2]] as const) {
    for (const [side, x] of [
      ["left", -spec.bodySizeM.x * ROAD_VEHICLE_WHEEL_HALF_TRACK_BODY_SCALE],
      ["right", spec.bodySizeM.x * ROAD_VEHICLE_WHEEL_HALF_TRACK_BODY_SCALE],
    ] as const) {
      const wheel = new Mesh(
        new CylinderGeometry(spec.wheelRadiusM, spec.wheelRadiusM, 0.22, 10),
        wheelMaterial.clone(),
      );
      wheel.name = `${axle}-wheel-${side}`;
      wheel.position.set(
        x,
        spec.wheelRadiusM - MAPPED_ROAD_VEHICLE_TIRE_VISUAL_SINK_M,
        z,
      );
      wheel.rotation.z = Math.PI / 2;
      wheel.castShadow = true;
      root.add(wheel);
    }
  }

  const headlampMaterial = new MeshStandardMaterial({
    color: 0xf2e9c9,
    emissive: 0xf2e9c9,
    emissiveIntensity: 0,
    roughness: 0.25,
  });
  const tailMaterial = new MeshStandardMaterial({
    color: 0x8b1915,
    emissive: 0x8b1915,
    emissiveIntensity: 0,
    roughness: 0.3,
  });
  for (const side of [-1, 1] as const) {
    const headlamp = new Mesh(
      new BoxGeometry(0.3, 0.14, 0.05),
      headlampMaterial.clone(),
    );
    headlamp.name = `headlamp:${side}`;
    headlamp.position.set(side * spec.bodySizeM.x * 0.31, 0.55, spec.bodySizeM.z * 0.505);
    root.add(headlamp);
    const tail = new Mesh(
      new BoxGeometry(0.3, 0.14, 0.05),
      tailMaterial.clone(),
    );
    tail.name = `tail-lamp:${side}`;
    tail.position.set(side * spec.bodySizeM.x * 0.31, 0.55, -spec.bodySizeM.z * 0.505);
    root.add(tail);
  }
  return root;
}

function createTrapezoidCabinGeometry(
  bottomWidthM: number,
  topWidthM: number,
  heightM: number,
  bottomLengthM: number,
  topLengthM: number,
): BufferGeometry {
  const bottomHalfWidth = bottomWidthM / 2;
  const topHalfWidth = topWidthM / 2;
  const bottomHalfLength = bottomLengthM / 2;
  const topHalfLength = topLengthM / 2;
  const halfHeight = heightM / 2;
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new Float32BufferAttribute([
    -bottomHalfWidth, -halfHeight, -bottomHalfLength,
    bottomHalfWidth, -halfHeight, -bottomHalfLength,
    bottomHalfWidth, -halfHeight, bottomHalfLength,
    -bottomHalfWidth, -halfHeight, bottomHalfLength,
    -topHalfWidth, halfHeight, -topHalfLength,
    topHalfWidth, halfHeight, -topHalfLength,
    topHalfWidth, halfHeight, topHalfLength,
    -topHalfWidth, halfHeight, topHalfLength,
  ], 3));
  geometry.setIndex([
    4, 6, 5, 4, 7, 6,
    3, 2, 6, 3, 6, 7,
    1, 0, 4, 1, 4, 5,
    2, 1, 5, 2, 5, 6,
    0, 3, 7, 0, 7, 4,
  ]);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function disposeProceduralRoadVehicleRoot(root: Object3D): void {
  root.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    object.geometry.dispose();
    const materials = Array.isArray(object.material)
      ? object.material
      : [object.material];
    for (const material of materials) material.dispose();
  });
}

function createVehicleVisual(root: Object3D, spec: Readonly<RoadVehicleSpec>): VehicleVisual {
  const wheels: WheelPivot[] = [];
  const steeringWheels: AnimatedNode[] = [];
  const headlamps: LampMaterial[] = [];
  const tailLamps: LampMaterial[] = [];
  const beaconLamps: LampMaterial[] = [];

  for (const level of [...root.children]) {
    wheels.push(...createWheelPivots(level));
    level.traverse((object) => {
      const name = object.name.toLowerCase();
      if (/(?:driver-)?steering-wheel/u.test(name)) {
        steeringWheels.push({
          object,
          baseQuaternion: object.quaternion.clone(),
        });
      }
      if (!(object instanceof Mesh)) return;
      if (/(?:headlamp|headlight)/u.test(name)) {
        headlamps.push(...cloneLampMaterials(object));
      } else if (/(?:tail-lamp|rear-lamp)/u.test(name)) {
        tailLamps.push(...cloneLampMaterials(object));
      } else if (/(?:lightbar.*(?:blue|emitter)|roof-beacon|grille-blue)/u.test(name)) {
        beaconLamps.push(...cloneLampMaterials(object));
      }
    });
  }

  const effects = createEffects(root, spec);
  return {
    root,
    wheels,
    steeringWheels,
    headlamps,
    tailLamps,
    beaconLamps,
    ...effects,
  };
}

function createWheelPivots(level: Object3D): readonly WheelPivot[] {
  const parts = new Map<string, Object3D[]>();
  level.traverse((object) => {
    if (!/(?:front|rear)-(?:tyre|hub|rim|wheel)/u.test(object.name.toLowerCase())) {
      return;
    }
    const side = object.position.x < 0 ? "left" : "right";
    const axle = object.name.toLowerCase().includes("front") ? "front" : "rear";
    const key = `${object.parent?.uuid ?? "root"}:${axle}:${side}`;
    const bucket = parts.get(key) ?? [];
    bucket.push(object);
    parts.set(key, bucket);
  });

  const pivots: WheelPivot[] = [];
  for (const [key, wheelParts] of parts) {
    const first = wheelParts[0];
    const parent = first?.parent;
    if (first === undefined || parent === null || parent === undefined) continue;
    const centre = first.position.clone();
    const steering = new Group();
    steering.name = `vehicle-wheel-steering:${key}`;
    steering.position.copy(centre);
    const rolling = new Group();
    rolling.name = `vehicle-wheel-rolling:${key}`;
    steering.add(rolling);
    parent.add(steering);
    for (const part of wheelParts) {
      part.position.sub(centre);
      rolling.add(part);
    }
    pivots.push({
      steering,
      rolling,
      baseY: centre.y,
      front: key.includes(":front:"),
    });
  }
  return pivots;
}

function cloneLampMaterials(mesh: Mesh): readonly LampMaterial[] {
  const source = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  const cloned = source.map((material) => material.clone());
  mesh.material = Array.isArray(mesh.material) ? cloned : cloned[0]!;
  return cloned.flatMap((material) => {
    if (!(material instanceof MeshStandardMaterial)) return [];
    return [{
      material,
      baseEmissive: material.emissive.clone(),
      baseIntensity: material.emissiveIntensity,
      side: mesh.position.x < 0 ? -1 as const : 1 as const,
    }];
  });
}

function createEffects(root: Object3D, spec: Readonly<RoadVehicleSpec>): Omit<
  VehicleVisual,
  "root" | "wheels" | "steeringWheels" | "headlamps" | "tailLamps" | "beaconLamps"
> {
  const width = spec.bodySizeM.x;
  const height = spec.bodySizeM.y;
  const length = spec.bodySizeM.z;
  const lampY = Math.max(spec.wheelRadiusM + 0.18, height * 0.42);
  const reverseMaterial = emissiveMaterial(0xf2f4df);
  const indicatorMaterial = emissiveMaterial(0xff8b16);
  const reverseLamps = createLampPair(
    root,
    reverseMaterial,
    width * 0.34,
    lampY,
    -length * 0.505,
    "reverse",
  );
  const frontIndicators = createLampPair(
    root,
    indicatorMaterial,
    width * 0.43,
    lampY,
    length * 0.507,
    "front-indicator",
  );
  const rearIndicators = createLampPair(
    root,
    indicatorMaterial,
    width * 0.43,
    lampY,
    -length * 0.507,
    "rear-indicator",
  );
  const leftIndicators = [frontIndicators[0]!, rearIndicators[0]!];
  const rightIndicators = [frontIndicators[1]!, rearIndicators[1]!];

  const headlights = [-1, 1].map((side) => {
    const light = new SpotLight(
      0xfff3ce,
      ROAD_VEHICLE_VISUAL_TUNING.headlightWorldIntensity,
      ROAD_VEHICLE_VISUAL_TUNING.headlightDistanceM,
      ROAD_VEHICLE_VISUAL_TUNING.headlightAngleRadians,
      0.55,
      1.4,
    );
    light.name = `vehicle-headlight:${side}`;
    light.position.set(side * width * 0.3, lampY, length * 0.48);
    const target = new Object3D();
    target.position.set(side * width * 0.16, lampY - 0.6, length + 20);
    root.add(target, light);
    light.target = target;
    light.visible = false;
    return light;
  });
  const beaconLight = new PointLight(
    0x176cff,
    ROAD_VEHICLE_VISUAL_TUNING.beaconWorldIntensity,
    ROAD_VEHICLE_VISUAL_TUNING.beaconDistanceM,
    1.6,
  );
  beaconLight.name = "vehicle-beacon-light";
  beaconLight.position.set(0, height + spec.wheelRadiusM + 0.25, 0);
  beaconLight.visible = false;
  root.add(beaconLight);

  const doors = ([-1, 1] as const).map((side) => {
    const doorHeight = Math.max(0.68, height * 0.65);
    const doorWidth = Math.min(1.25, length * 0.26);
    const pivot = new Group();
    pivot.name = side < 0 ? "vehicle-door:left" : "vehicle-door:right";
    pivot.position.set(
      side * (width / 2 + 0.035),
      spec.wheelRadiusM + doorHeight * 0.52,
      Math.min(length * 0.28, spec.seats[0]?.localPosition.z ?? length * 0.18) + doorWidth / 2,
    );
    const panel = new Mesh(
      new BoxGeometry(0.055, doorHeight, doorWidth),
      new MeshStandardMaterial({
        color: spec.proceduralBodyColorHex ??
          ROAD_VEHICLE_BODY_COLORS_BY_ID[spec.id] ?? 0xc8cbc5,
        roughness: 0.48,
        metalness: 0.18,
      }),
    );
    panel.name = `${pivot.name}:panel`;
    panel.position.z = -doorWidth / 2;
    panel.castShadow = true;
    pivot.add(panel);
    pivot.visible = false;
    root.add(pivot);
    return { pivot, side: side < 0 ? "left" as const : "right" as const };
  });

  const patchMaterial = new MeshStandardMaterial({
    color: 0x261a16,
    roughness: 0.92,
    transparent: true,
    opacity: 0,
    depthWrite: false,
  });
  const damagePatches = [-0.24, 0.26].map((x, index) => {
    const patch = new Mesh(
      new BoxGeometry(width * 0.24, Math.max(0.14, height * 0.17), 0.025),
      patchMaterial.clone(),
    );
    patch.name = `vehicle-damage-patch:${index}`;
    patch.position.set(x * width, lampY, length * 0.506);
    patch.visible = false;
    root.add(patch);
    return patch;
  });

  const impactParticles: Mesh[] = [];
  for (let index = 0; index < ROAD_VEHICLE_VISUAL_TUNING.impactParticleCount; index += 1) {
    const spark = index % 2 === 0;
    const particle = new Mesh(
      spark ? new BoxGeometry(0.025, 0.025, 0.18) : new SphereGeometry(0.09, 5, 4),
      new MeshStandardMaterial({
        color: spark ? 0xffa229 : 0x9d8d78,
        emissive: spark ? 0xff5a08 : 0x000000,
        emissiveIntensity: spark ? 3 : 0,
        transparent: true,
        opacity: 0.8,
      }),
    );
    particle.name = `vehicle-impact-particle:${index}`;
    particle.visible = false;
    root.add(particle);
    impactParticles.push(particle);
  }

  return {
    reverseLamps,
    leftIndicators,
    rightIndicators,
    doors,
    headlights,
    beaconLight,
    damagePatches,
    impactParticles,
  };
}

function createLampPair(
  root: Object3D,
  material: MeshStandardMaterial,
  x: number,
  y: number,
  z: number,
  name: string,
): readonly [Mesh, Mesh] {
  const geometry = new BoxGeometry(0.16, 0.11, 0.04);
  const createLamp = (positionX: number, side: "left" | "right"): Mesh => {
    const lamp = new Mesh(geometry, material.clone());
    lamp.name = `vehicle-${name}:${side}`;
    lamp.position.set(positionX, y, z);
    lamp.visible = false;
    root.add(lamp);
    return lamp;
  };
  return [createLamp(-x, "left"), createLamp(x, "right")];
}

function emissiveMaterial(color: number): MeshStandardMaterial {
  return new MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0,
    roughness: 0.28,
    metalness: 0.05,
  });
}

function updateVehicleVisual(
  visual: VehicleVisual,
  presentation: Readonly<RoadVehiclePresentation>,
): void {
  visual.root.position.set(
    presentation.position.x,
    presentation.position.y - presentation.suspensionCompressionM,
    presentation.position.z,
  );
  visual.root.rotation.set(
    presentation.pitchRadians + presentation.visualPitchRadians,
    presentation.yawRadians,
    presentation.rollRadians + presentation.visualRollRadians,
    "YXZ",
  );
  for (const wheel of visual.wheels) {
    wheel.steering.position.y = wheel.baseY + presentation.suspensionCompressionM;
    wheel.steering.rotation.y = wheel.front ? presentation.steeringRadians : 0;
    wheel.rolling.rotation.x = presentation.wheelRotationRadians;
  }
  scratchQuaternion.setFromAxisAngle(
    rollAxis,
    -presentation.steeringRadians * ROAD_VEHICLE_VISUAL_TUNING.steeringWheelRatio,
  );
  for (const steeringWheel of visual.steeringWheels) {
    steeringWheel.object.quaternion
      .copy(steeringWheel.baseQuaternion)
      .multiply(scratchQuaternion);
  }

  setLampMaterials(
    visual.headlamps,
    presentation.headlightsOn,
    0xffedbd,
    ROAD_VEHICLE_VISUAL_TUNING.headlightEmissiveIntensity,
  );
  setLampMaterials(
    visual.tailLamps,
    presentation.brakeLightsOn || presentation.headlightsOn,
    0xff170d,
    presentation.brakeLightsOn
      ? ROAD_VEHICLE_VISUAL_TUNING.brakeTailEmissiveIntensity
      : ROAD_VEHICLE_VISUAL_TUNING.runningTailEmissiveIntensity,
  );
  for (const light of visual.headlights) light.visible = presentation.headlightsOn;
  for (const lamp of visual.reverseLamps) {
    lamp.visible = presentation.reverseLightsOn;
    setMeshEmissive(lamp, presentation.reverseLightsOn
      ? ROAD_VEHICLE_VISUAL_TUNING.reverseLampEmissiveIntensity
      : 0);
  }

  const flashOn = presentation.indicatorPhase01 < 0.5;
  setIndicatorMeshes(visual.leftIndicators, presentation.leftIndicatorOn && flashOn);
  setIndicatorMeshes(visual.rightIndicators, presentation.rightIndicatorOn && flashOn);
  const beaconFirstSide = presentation.indicatorPhase01 < 0.25 ||
    presentation.indicatorPhase01 >= 0.75;
  for (const lamp of visual.beaconLamps) {
    const activeSide = beaconFirstSide ? -1 : 1;
    const active = presentation.beaconOn && lamp.side === activeSide;
    lamp.material.emissive.setHex(active ? 0x176cff : lamp.baseEmissive.getHex());
    lamp.material.emissiveIntensity = active
      ? ROAD_VEHICLE_VISUAL_TUNING.beaconEmissiveIntensity
      : lamp.baseIntensity;
  }
  visual.beaconLight.visible = presentation.beaconOn;
  visual.beaconLight.intensity = flashOn
    ? ROAD_VEHICLE_VISUAL_TUNING.beaconWorldIntensity
    : 0;

  for (const door of visual.doors) {
    const active = door.side === presentation.activeDoorSide && presentation.doorOpen01 > 0.001;
    door.pivot.visible = active;
    door.pivot.rotation.y = active
      ? (door.side === "left" ? 1 : -1) *
        ROAD_VEHICLE_VISUAL_TUNING.maximumDoorOpenRadians *
        presentation.doorOpen01
      : 0;
  }
  for (const patch of visual.damagePatches) {
    patch.visible = presentation.damage01 > 0.02;
    const material = patch.material;
    if (material instanceof MeshStandardMaterial) {
      material.opacity = presentation.damage01 *
        ROAD_VEHICLE_VISUAL_TUNING.damagePatchMaximumOpacity;
    }
  }
  for (const [index, particle] of visual.impactParticles.entries()) {
    const intensity = presentation.impactIntensity01;
    particle.visible = intensity > 0.02;
    const angle = index / visual.impactParticles.length * Math.PI * 2;
    const spread = intensity * (0.35 + index * 0.07);
    particle.position.set(
      Math.cos(angle) * spread,
      Math.max(0.08, Math.sin(angle * 1.7) * spread + 0.25),
      presentation.speedMps < 0 ? 1.1 : -1.1,
    );
    particle.scale.setScalar(0.7 + intensity * 1.5);
  }
}

function setLampMaterials(
  lamps: readonly LampMaterial[],
  active: boolean,
  color: number,
  intensity: number,
): void {
  for (const lamp of lamps) {
    lamp.material.emissive.setHex(active ? color : lamp.baseEmissive.getHex());
    lamp.material.emissiveIntensity = active ? intensity : lamp.baseIntensity;
  }
}

function setIndicatorMeshes(meshes: readonly Mesh[], active: boolean): void {
  for (const mesh of meshes) {
    mesh.visible = active;
    setMeshEmissive(
      mesh,
      active ? ROAD_VEHICLE_VISUAL_TUNING.indicatorEmissiveIntensity : 0,
    );
  }
}

function setMeshEmissive(mesh: Mesh, intensity: number): void {
  const material = mesh.material;
  if (material instanceof MeshStandardMaterial) material.emissiveIntensity = intensity;
}
